import {
  DEFAULT_STOREFRONT_TEMPLATE_ID,
  type StorefrontTemplateId,
  storefrontTemplateIdSchema,
} from "@loot-os/api-contract";

import type { TenantConfig } from "../lib/tenant.js";
import { ClassicBoxesBreadcrumb } from "./classic/boxes/ClassicBoxesBreadcrumb.js";
import { ClassicBoxesLayout } from "./classic/boxes/ClassicBoxesLayout.js";
import { ClassicBoxesTitle } from "./classic/boxes/ClassicBoxesTitle.js";
import { ClassicBoxFilters } from "./classic/boxes/ClassicBoxFilters.js";
import { ClassicBoxGrid } from "./classic/boxes/ClassicBoxGrid.js";
import { ClassicBoxTabs } from "./classic/boxes/ClassicBoxTabs.js";
import { ClassicTopHits } from "./classic/boxes/ClassicTopHits.js";
import { ClassicFaq } from "./classic/home/ClassicFaq.js";
import { ClassicFeaturedBoxes } from "./classic/home/ClassicFeaturedBoxes.js";
import { ClassicHero } from "./classic/home/ClassicHero.js";
import { ClassicHomeLayout } from "./classic/home/ClassicHomeLayout.js";
import { ClassicPromoCards } from "./classic/home/ClassicPromoCards.js";
import { ClassicRecentOpens } from "./classic/home/ClassicRecentOpens.js";
import type { StorefrontTemplate } from "./contract.js";

/**
 * Every storefront template the SPA can render, by the ids api-contract lists: a listed id with
 * no implementation here fails to compile, and so does a template missing a page. Adding one is a
 * folder beside `classic/`, an id in `STOREFRONT_TEMPLATE_IDS` and an entry here; every page's
 * parity suite runs against it from then on.
 */
export const STOREFRONT_TEMPLATES = {
  classic: {
    home: {
      Layout: ClassicHomeLayout,
      sections: {
        hero: ClassicHero,
        promoCards: ClassicPromoCards,
        recentOpens: ClassicRecentOpens,
        featuredBoxes: ClassicFeaturedBoxes,
        faq: ClassicFaq,
      },
    },
    boxes: {
      Layout: ClassicBoxesLayout,
      sections: {
        breadcrumb: ClassicBoxesBreadcrumb,
        title: ClassicBoxesTitle,
        tabs: ClassicBoxTabs,
        filters: ClassicBoxFilters,
        topHits: ClassicTopHits,
        grid: ClassicBoxGrid,
      },
    },
  },
} as const satisfies Record<StorefrontTemplateId, StorefrontTemplate>;

/** The environment's chosen template; the default for a config cached before the field existed. */
export function storefrontTemplateOf(config: TenantConfig): StorefrontTemplate {
  const id = storefrontTemplateIdSchema.safeParse((config as Partial<TenantConfig>).template);
  return STOREFRONT_TEMPLATES[id.success ? id.data : DEFAULT_STOREFRONT_TEMPLATE_ID];
}
